/**
 * SplatClipExport — 按裁剪体积筛选高斯 splat 并导出文件。
 *
 * 主入口 `exportClippedGaussianSplats(viewer, options)`：
 *   - 筛选：与点云裁剪同源的 6 平面 OBB 判定（平面构造同 VolumePanel.download
 *     的 legacy 服务端导出路径，VolumePanel.js:185-208），对每个 splat 的中心点
 *     做世界空间判定。不读 spark 的渲染期 SDF（SplatClipBridge 是视觉镜像，
 *     语义被降级过，导出必须以判定源为准）。
 *   - mode: 'inside'  → 保留位于任一/全部体积内的 splat（对应 ClipTask.SHOW_INSIDE，
 *     多盒并集，即 viewer.clipMethod 默认的 INSIDE_ANY 语义）
 *     'outside' → 保留位于所有体积之外的 splat（对应 ClipTask.SHOW_OUTSIDE）
 *   - format: 'splat'（antimatter15 裸格式，32B/splat，spark 的 decodeAntiSplat
 *     可原样读回）| 'ply'（binary_little_endian，全 float 属性，SuperSplat 等工具
 *     可直接打开；f_dc/opacity/scale 按 3DGS PLY 约定写出 DC/sigmoid 逆/log）
 *
 * 数据面（spark v2.1.0，已源码核实）：
 *   - SplatMesh.forEachSplat(cb(index, center, scales, quaternion, opacity, color))
 *     对 PackedSplats 与 ExtSplats 两种底层一致可用（SplatMesh.ts:554-565 委托）；
 *     回调对象是模块级复用的静态对象，必须就地消费，不可保存引用。
 *   - packedSplats 的 center 是 mesh 局部坐标（f16），世界坐标需自乘 matrixWorld；
 *     scales/quaternion 同理要吃 mesh 的旋转/均匀缩放。
 *   - 分页 mesh（.rad / paged）的 splats 是 PagedSplats，packedArray 只是当前页，
 *     导出前用 spark 命名空间做 instanceof 检查并跳过。
 *
 * 本模块刻意不顶层 import spark（保持 spark 懒加载语义），spark 命名空间从
 * viewer.__sparkModule 取（PotreeSparkAdapter.ensureSparkRenderer 缓存）——因此
 * 本文件可在 Node 里直接 import 做单元验证。
 */
import * as THREE from "../../../libs/three.js/build/three.module.js";
import { BoxVolume, YDBoxVolume } from "../../utils/Volume.js";
import { YDPolygonVolume, pointInPolygonPrism } from "../../utils/YDPolygonVolume.js";

/** 3DGS PLY 的 SH 基频系数，color = f_dc * SH_C0 + 0.5（spark ply.ts:719 同值）。 */
const SH_C0 = 0.28209479177387814;
/** spark 的 scale 量化下限（defines.ts LN_SCALE_MIN）。PLY 写 ln(scale) 时防 -inf。 */
const LN_SCALE_MIN = -12.0;
/** PLY 每 splat 的 float 数：3 pos + 3 f_dc + 1 opacity + 3 scale + 4 rot = 14（56 字节）。
 *  必须与 header 的 property 行数严格一致，否则每行错位、第 2 个 splat 起全是乱码。 */
const PLY_FLOATS_PER_SPLAT = 14;

// ─────────────────────────────────────────────────────────────────────────────
// 体积 → 平面
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 由体积的 matrixWorld 构造 6 个裁剪平面（法线朝内，盒内点距离 > 0）。
 * 与 VolumePanel.download 的服务端导出构造完全一致：BoxVolume/YDBoxVolume 的
 * matrixWorld 携带的是「全宽」约定（局部单位立方体 ±0.5），±0.5 的局部偏移
 * 恰好落在 6 个面上。
 *
 * @param {object} volume 任意带 updateMatrixWorld()/matrixWorld 的 Object3D
 *   （真实调用传 BoxVolume/YDBoxVolume；测试传 Object3D 即可）
 * @returns {THREE.Plane[]} 6 个平面
 */
export function _planesFromVolume(volume) {
	volume.updateMatrixWorld();
	const toClip = volume.matrixWorld;

	const px = new THREE.Vector3(+0.5, 0, 0).applyMatrix4(toClip);
	const nx = new THREE.Vector3(-0.5, 0, 0).applyMatrix4(toClip);
	const py = new THREE.Vector3(0, +0.5, 0).applyMatrix4(toClip);
	const ny = new THREE.Vector3(0, -0.5, 0).applyMatrix4(toClip);
	const pz = new THREE.Vector3(0, 0, +0.5).applyMatrix4(toClip);
	const nz = new THREE.Vector3(0, 0, -0.5).applyMatrix4(toClip);

	const pxN = new THREE.Vector3().subVectors(nx, px).normalize();
	const nxN = pxN.clone().multiplyScalar(-1);
	const pyN = new THREE.Vector3().subVectors(ny, py).normalize();
	const nyN = pyN.clone().multiplyScalar(-1);
	const pzN = new THREE.Vector3().subVectors(nz, pz).normalize();
	const nzN = pzN.clone().multiplyScalar(-1);

	return [
		new THREE.Plane().setFromNormalAndCoplanarPoint(pxN, px),
		new THREE.Plane().setFromNormalAndCoplanarPoint(nxN, nx),
		new THREE.Plane().setFromNormalAndCoplanarPoint(pyN, py),
		new THREE.Plane().setFromNormalAndCoplanarPoint(nyN, ny),
		new THREE.Plane().setFromNormalAndCoplanarPoint(pzN, pz),
		new THREE.Plane().setFromNormalAndCoplanarPoint(nzN, nz),
	];
}

/**
 * 中心点是否在某组 6 平面内（全部平面距离 > 0）。
 * 与裁剪 shader 的 inverse-matrix 判定在刚体+缩放变换下等价。
 */
function _insidePlanes(p, planes) {
	for (let i = 0; i < planes.length; i++) {
		if (planes[i].distanceToPoint(p) <= 0) return false;
	}
	return true;
}

/**
 * 由体积构造「世界点 → 是否在体积内」的判定闭包：
 * - BoxVolume/YDBoxVolume → 6 平面（_planesFromVolume）；
 * - YDPolygonVolume → 世界点经 matrixWorld 逆矩阵变回局部后做
 *   pointInPolygonPrism（局部 XY even-odd + 局部 Z 范围，与点云 shader 同语义）。
 */
function _testFromVolume(v) {
	if (typeof YDPolygonVolume !== "undefined" && v instanceof YDPolygonVolume) {
		v.updateMatrixWorld();
		const inverse = v.matrixWorld.clone().invert();
		const local = new THREE.Vector3();
		return (p) => {
			local.copy(p).applyMatrix4(inverse);
			return pointInPolygonPrism(local, v.points, v.zMin, v.zMax);
		};
	}
	const planes = _planesFromVolume(v);
	return (p) => _insidePlanes(p, planes);
}

/** 多体积判定：any = 并集（INSIDE_ANY），all = 交集（INSIDE_ALL）。 */
function _insideVolumeSet(p, tests, clipMethod) {
	if (tests.length === 0) return false;
	if (clipMethod === "all") {
		for (let i = 0; i < tests.length; i++) {
			if (!tests[i](p)) return false;
		}
		return true;
	}
	for (let i = 0; i < tests.length; i++) {
		if (tests[i](p)) return true;
	}
	return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// 筛选
// ─────────────────────────────────────────────────────────────────────────────

const _wp = new THREE.Vector3();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scl = new THREE.Vector3();

/**
 * 遍历一个 SplatMesh，返回被保留的（mesh 局部）索引。
 * mesh 的世界旋转/均匀缩放不参与筛选（筛选用中心点，与点云一致），
 * 只在导出编码时参与。
 */
function _collectIndices(mesh, volumeTests, mode, clipMethod) {
	const indices = [];
	mesh.forEachSplat((index, center) => {
		_wp.copy(center).applyMatrix4(mesh.matrixWorld);
		const inside = _insideVolumeSet(_wp, volumeTests, clipMethod);
		if (mode === "inside" ? inside : !inside) {
			indices.push(index);
		}
	});
	return indices;
}

/**
 * mesh.matrixWorld 分解。均匀缩放时 scales ×= s、quaternion ×= meshQuat 是精确的；
 * 非均匀缩放下「旋转后的各向异性椭球」无法用单个 scale+quaternion 表达，
 * 只能近似（取几何均值），warn 一次。返回 null 表示恒等变换。
 */
function _decomposeMeshTransform(mesh, warnedRef) {
	mesh.updateMatrixWorld();
	mesh.matrixWorld.decompose(_pos, _quat, _scl);
	const isIdentity =
		Math.abs(_pos.x) + Math.abs(_pos.y) + Math.abs(_pos.z) < 1e-9 &&
		Math.abs(_quat.x) + Math.abs(_quat.y) + Math.abs(_quat.z) < 1e-9 &&
		Math.abs(_scl.x - 1) + Math.abs(_scl.y - 1) + Math.abs(_scl.z - 1) < 1e-9;
	if (isIdentity) return null;

	const maxS = Math.max(_scl.x, _scl.y, _scl.z);
	const minS = Math.min(_scl.x, _scl.y, _scl.z);
	let uniformScale = null;
	if (maxS - minS > maxS * 0.01) {
		if (!warnedRef.warned) {
			warnedRef.warned = true;
			console.warn(
				"[SplatClipExport] mesh 存在非均匀缩放 (" +
				[_scl.x, _scl.y, _scl.z].map(v => v.toFixed(3)).join(", ") +
				")，导出的 splat 形状只能近似（scale 取几何均值）。"
			);
		}
		uniformScale = Math.cbrt(Math.abs(_scl.x * _scl.y * _scl.z)) || 1;
	} else {
		uniformScale = (_scl.x + _scl.y + _scl.z) / 3;
	}
	return { quaternion: _quat.clone(), uniformScale };
}

// ─────────────────────────────────────────────────────────────────────────────
// 序列化（两个 encoder 都以「再次 forEachSplat + 写游标」消费 indices）
// ─────────────────────────────────────────────────────────────────────────────

/**
 * .splat（antimatter15）编码：每 splat 32 字节
 *   f32 x,y,z, sx,sy,sz | u8 r,g,b,a | u8 qw,qx,qy,qz（(v-128)/128，w 在前）
 * 与 spark decodeAntiSplat（antisplat.ts:24-48）严格对偶，可原样读回。
 */
export function _encodeSplatBytes(mesh, indices, xf) {
	const n = indices.length;
	const buffer = new ArrayBuffer(n * 32);
	const f32 = new Float32Array(buffer);
	const u8 = new Uint8Array(buffer);
	let cursor = 0; // 已写出的 splat 数

	mesh.forEachSplat((index, center, scales, quaternion, opacity, color) => {
		if (cursor >= n || indices[cursor] !== index) return;
		const o = cursor * 8; // 每 splat 8 个 float32 槽（前 6 用 + 后 2 只用一半）
		const s = xf ? xf.uniformScale : 1;
		f32[o + 0] = center.x; f32[o + 1] = center.y; f32[o + 2] = center.z;
		f32[o + 3] = scales.x * s; f32[o + 4] = scales.y * s; f32[o + 5] = scales.z * s;
		const b = cursor * 32; // 每 splat 32 字节
		u8[b + 24] = _toU8(color.r); u8[b + 25] = _toU8(color.g);
		u8[b + 26] = _toU8(color.b); u8[b + 27] = _toU8(opacity);
		// 四元数吃 mesh 旋转：qWorld = meshQuat × qLocal
		const q = xf ? _tmpQuat.multiplyQuaternions(xf.quaternion, quaternion) : quaternion;
		u8[b + 28] = _quatToU8(q.w); u8[b + 29] = _quatToU8(q.x);
		u8[b + 30] = _quatToU8(q.y); u8[b + 31] = _quatToU8(q.z);
		cursor++;
	});
	return { buffer, count: cursor };
}
const _tmpQuat = new THREE.Quaternion();

const _toU8 = (v) => Math.max(0, Math.min(255, Math.round(v * 255)));
const _quatToU8 = (v) => Math.max(0, Math.min(255, Math.round(v * 128) + 128));
const _logit = (o) => {
	const c = Math.max(1e-6, Math.min(1 - 1e-6, o));
	return Math.log(c / (1 - c));
};

/**
 * 3DGS PLY（binary_little_endian）编码：全 float 属性，无量化歧义。
 * 每 splat 14 × f32 = 56 字节，属性顺序（读者按 header 解析，此处取主流顺序）：
 *   x y z | f_dc_0..2 | opacity | scale_0..2 | rot_0..3（w,x,y,z）
 * 逆变换与 spark ply.ts 的解码严格对偶：
 *   f_dc = (color - 0.5) / SH_C0；opacity = logit；scale = ln(scale)。
 */
export function _encodePlyBytes(mesh, indices, xf) {
	const n = indices.length;
	const header =
		"ply\n" +
		"format binary_little_endian 1.0\n" +
		"comment generated by potree_webgl SplatClipExport\n" +
		`element vertex ${n}\n` +
		"property float x\nproperty float y\nproperty float z\n" +
		"property float f_dc_0\nproperty float f_dc_1\nproperty float f_dc_2\n" +
		"property float opacity\n" +
		"property float scale_0\nproperty float scale_1\nproperty float scale_2\n" +
		"property float rot_0\nproperty float rot_1\nproperty float rot_2\nproperty float rot_3\n" +
		"end_header\n";
	const headerBytes = new TextEncoder().encode(header);
	const body = new ArrayBuffer(n * PLY_FLOATS_PER_SPLAT * 4);
	const f32 = new Float32Array(body);
	let cursor = 0;

	mesh.forEachSplat((index, center, scales, quaternion, opacity, color) => {
		if (cursor >= n || indices[cursor] !== index) return;
		const o = cursor * PLY_FLOATS_PER_SPLAT;
		const s = xf ? xf.uniformScale : 1;
		f32[o + 0] = center.x; f32[o + 1] = center.y; f32[o + 2] = center.z;
		f32[o + 3] = (color.r - 0.5) / SH_C0;
		f32[o + 4] = (color.g - 0.5) / SH_C0;
		f32[o + 5] = (color.b - 0.5) / SH_C0;
		f32[o + 6] = _logit(opacity);
		f32[o + 7] = Math.log(Math.max(scales.x * s, Math.exp(LN_SCALE_MIN)));
		f32[o + 8] = Math.log(Math.max(scales.y * s, Math.exp(LN_SCALE_MIN)));
		f32[o + 9] = Math.log(Math.max(scales.z * s, Math.exp(LN_SCALE_MIN)));
		const q = xf ? _tmpQuat.multiplyQuaternions(xf.quaternion, quaternion) : quaternion;
		f32[o + 10] = q.w; f32[o + 11] = q.x; f32[o + 12] = q.y; f32[o + 13] = q.z;
		cursor++;
	});

	const out = new Uint8Array(headerBytes.length + body.byteLength);
	out.set(headerBytes, 0);
	out.set(new Uint8Array(body), headerBytes.length);
	return { buffer: out.buffer, count: cursor, headerBytes: headerBytes.length };
}

// ─────────────────────────────────────────────────────────────────────────────
// 主入口
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 按裁剪体积筛选高斯 splat 并导出。
 *
 * @param {object} viewer Potree.Viewer
 * @param {object} [options]
 * @param {object[]} [options.volumes] 参与判定的体积。默认
 *   scene.volumes 里 clip===true 的 BoxVolume/YDBoxVolume/YDPolygonVolume（与
 *   viewer.js 点云裁剪的收集一致；SphereVolume/其它类型不参与）。
 * @param {'inside'|'outside'} [options.mode='inside']
 *   inside = 导出体积内的 splat；outside = 导出体积外的 splat。
 * @param {'any'|'all'} [options.clipMethod='any'] 多体积时 any=并集（对应
 *   viewer.clipMethod 默认 INSIDE_ANY），all=交集。
 * @param {'splat'|'ply'} [options.format='splat']
 * @param {string} [options.filename] 不含扩展名，默认 `gaussian_clip`。
 * @param {object[]} [options.meshes] 要导出的 SplatMesh 列表，默认
 *   scene.gaussianSplats 全部。
 * @param {boolean} [options.download=true] false = 只统计并返回 bytes（干跑）。
 * @returns {Promise<{format:string, mode:string, totalSplats:number,
 *   exportedSplats:number, meshes:{name:string,total:number,exported:number}[],
 *   filename:string, bytes:ArrayBuffer|null}>}
 */
export async function exportClippedGaussianSplats(viewer, options = {}) {
	const {
		volumes,
		mode = "inside",
		clipMethod = "any",
		format = "splat",
		filename = "gaussian_clip",
		meshes,
		download = true,
	} = options;

	const scene = viewer.scene;
	const volList = volumes || (scene.volumes || []).filter(
		(v) => v && v.clip === true && (v instanceof YDBoxVolume || v instanceof BoxVolume || v instanceof YDPolygonVolume)
	);
	if (volList.length === 0) {
		throw new Error("[SplatClipExport] 没有可用的裁剪体积：请先创建 clip=true 的 BoxVolume/YDBoxVolume/YDPolygonVolume，或显式传入 options.volumes。");
	}
	const volumeTests = volList.map((v) => _testFromVolume(v));

	const meshList = meshes || (scene.gaussianSplats || []);
	if (meshList.length === 0) {
		throw new Error("[SplatClipExport] 场景中没有已加载的高斯模型（scene.gaussianSplats 为空）。");
	}

	const spark = viewer.__sparkModule || null;
	const warnedRef = { warned: false };
	const perMesh = [];
	let totalSplats = 0;
	let exportedSplats = 0;
	let result = null;

	for (const mesh of meshList) {
		if (!mesh || typeof mesh.forEachSplat !== "function") {
			console.warn("[SplatClipExport] 跳过非 SplatMesh 对象:", mesh && mesh.name);
			continue;
		}
		// 分页 mesh（.rad / paged）只持有当前页数据，导出会静默丢大部分 splat —— 拒绝。
		const splats = mesh.splats;
		const isFull =
			!spark ||           // 命名空间拿不到时退化为鸭子类型（forEachSplat 已过）
			splats instanceof spark.PackedSplats ||
			splats instanceof spark.ExtSplats;
		if (!isFull) {
			console.warn(
				`[SplatClipExport] 跳过分页/流式 mesh "${mesh.name || mesh.uuid}"：` +
				"分页数据只持有当前页，全量导出请使用非分页格式（.ply/.spz/.splat/.ksplat）。"
			);
			continue;
		}

		const total = mesh.numSplats || 0;
		const indices = _collectIndices(mesh, volumeTests, mode, clipMethod);
		const xf = _decomposeMeshTransform(mesh, warnedRef);

		let encoded;
		if (format === "ply") {
			encoded = _encodePlyBytes(mesh, indices, xf);
		} else {
			encoded = _encodeSplatBytes(mesh, indices, xf);
		}
		if (encoded.count !== indices.length) {
			console.warn(
				`[SplatClipExport] mesh "${mesh.name}": 编码数 ${encoded.count} != 筛选数 ${indices.length}，` +
				"可能是遍历期间数据被改写。"
			);
		}

		totalSplats += total;
		exportedSplats += encoded.count;
		perMesh.push({ name: mesh.name || mesh.uuid || "SplatMesh", total, exported: encoded.count });

		// 多 mesh 时只把最后一个的 bytes 交给下载/返回（导出按「当前选中集」用，
		// 常见场景只有一个 splat 模型；多模型请分次调用并传 options.meshes）。
		result = encoded;
	}

	const ext = format === "ply" ? "ply" : "splat";
	const finalName = `${filename}.${ext}`;

	if (download && typeof Blob !== "undefined" && result && result.count > 0) {
		const blob = new Blob([result.buffer], { type: "application/octet-stream" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = finalName;
		a.click();
		setTimeout(() => URL.revokeObjectURL(url), 10_000);
	}

	return {
		format,
		mode,
		totalSplats,
		exportedSplats,
		meshes: perMesh,
		filename: finalName,
		bytes: download ? null : (result ? result.buffer : null),
	};
}
